import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const ContentComplaintsList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [contentComplaints, setContentComplaints] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} content complaints`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: ''
  });

  const getContentComplaints = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching content complaints with params:', requestParams);
      const response = await masterService.getContentComplaints(requestParams);
      console.log('Content Complaints API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const contentComplaintsData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Content Complaints data:', contentComplaintsData);
        console.log('Pagination data:', paginationData);
        
        // Ensure contentComplaintsData is an array
        if (Array.isArray(contentComplaintsData)) {
          setContentComplaints(contentComplaintsData);
        } else {
          console.error('Content Complaints data is not an array:', contentComplaintsData);
          setContentComplaints([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
        message.error('Failed to fetch content complaints');
        setContentComplaints([]);
      }
    } catch (error) {
      console.error('Error fetching content complaints:', error);
      message.error('Failed to fetch content complaints');
      setContentComplaints([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText]);

  // Load content complaints data
  useEffect(() => {
    getContentComplaints();
  }, [getContentComplaints]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getContentComplaints({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getContentComplaints({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add content complaint
  const handleAddContentComplaint = () => {
    navigate('/master/addContentComplaints');
  };

  // Handle edit content complaint
  const handleEditContentComplaint = (contentId) => {
    navigate(`/master/editContentComplaints/${contentId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Content Name',
      dataIndex: 'content_name',
      key: 'content_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.content_name.localeCompare(b.content_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      width: 100,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditContentComplaint(record.content_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Content Complaints</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Content Complaints List"
      desc="Manage content complaints and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Content Complaints ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContentComplaint}
              size="large"
            >
              Add Content Complaint
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search content complaints..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={contentComplaints}
          rowKey="content_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No content complaints found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default ContentComplaintsList;